import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetProjectMutation,
  useUpdateProjectNameMutation,
} from "../../redux/slices/projectApiSlice";
import ProjectIntegrationForm from "./forms/projectIntegrationForm";
import ProjectSettingsForm from "./forms/projectSettingsForm";
import { CustomLoader } from "../../components";
import { CiPlug1 } from "react-icons/ci";
import { FaRegCircleCheck } from "react-icons/fa6";
import { LoadingButton } from "@mui/lab";
import SelectPlatform from "./selectPlatform";
import { FaRegEdit, FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";

const Project = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [project, setProject] = useState(null);
  const [getProject, { isLoading: getLoading }] = useGetProjectMutation();
  const [updateProjectName, { isLoading: updateLoading }] =
    useUpdateProjectNameMutation();

  const [openPlatformDialog, setOpenPlatformDialog] = useState(false);
  const handleOpenCMS = () => setOpenPlatformDialog(true);
  const handleCloseCMS = () => setOpenPlatformDialog(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editableName, setEditableName] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  // Confirmation dialog
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  // Fetch project
  const handleGetSingle = async (id) => {
    try {
      const res = await getProject({ id }).unwrap();
      const sanitizedData = Object.fromEntries(
        Object.entries(res?.data || {}).map(([key, value]) => [
          key,
          value === null ? "" : value,
        ])
      );
      setProject(sanitizedData);
      setEditableName(sanitizedData.name || "");
    } catch (error) {
      console.error("Error fetching project:", error);
    }
  };

  useEffect(() => {
    handleGetSingle(id);
  }, [id]);

  // Handle update name
  const handleUpdateName = async () => {
    if (!editableName.trim()) {
      setError(
        t("Website name cannot be empty", "Le nom du site ne peut pas être vide")
      );
      return;
    }

    try {
      await updateProjectName({
        id,
        data: { name: editableName },
      }).unwrap();

      setProject((prev) => ({ ...prev, name: editableName }));
      setIsEditing(false);
      setError("");

      toast.success(
        t(
          "Website name updated successfully!",
          "Nom du site mis à jour avec succès !"
        )
      );
    } catch (error) {
      toast.error(
        t(
          "Failed to update website name. Please try again.",
          "Échec de la mise à jour du nom du site. Veuillez réessayer."
        )
      );
    }
  };

  // Blur handler
  const handleBlur = () => {
    if (!editableName.trim()) {
      setError(
        t("Website name cannot be empty", "Le nom du site ne peut pas être vide")
      );
      inputRef.current?.focus();
      return;
    }

    if (editableName !== project?.name) {
      setOpenConfirmDialog(true);
    } else {
      setError("");
      setIsEditing(false);
    }
  };

  // Confirm dialog handlers
  const handleConfirmChange = async () => {
    setOpenConfirmDialog(false);
    await handleUpdateName();
  };

  const handleKeepPrevious = () => {
    setEditableName(project?.name);
    setIsEditing(false);
    setOpenConfirmDialog(false);
  };

  if (getLoading || project === null) {
    return (
      <div className="flex items-center">
        <CustomLoader />
      </div>
    );
  }

  const { platform, name, url, favicon, integration_data, export_data } =
    project || {};
  const projectInfos =
    platform && name && url ? { platform, name, url, favicon } : null;

  // ✅ FIXED: Proper CMS connection condition
  const isCMSConnected =
    (projectInfos?.platform === "wordpress" &&
      integration_data?.username) ||
    (projectInfos?.platform === "shopify" &&
      integration_data?.access_token) ||
    (projectInfos?.platform === "wix" &&
      integration_data?.api_key);

  return (
    <div className="mt-3 px-2 pl-6 pr-6">
      <Helmet>
        <title>
          {t("Website")} - {projectInfos?.name || "CMS"}
        </title>
        <meta
          name="description"
          content="Vision générale de votre compte et de vos activités."
        />
      </Helmet>

      {/* BACK BUTTON */}
      <div className="flex items-center justify-start mb-2">
        <button
          onClick={() => navigate("/projects")}
          className="flex items-center text-blue-500 hover:text-blue-700 font-medium border-none bg-transparent"
        >
          <FaArrowLeft className="text-sm mr-1" />
          {t("Back to Websites", "Retour aux sites Web")}
        </button>
      </div>

      {/* WEBSITE INFO */}
      {projectInfos && (
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center">
            <img
              src={projectInfos.favicon}
              alt={`${projectInfos.name} favicon`}
              className="w-10 h-10 mr-4"
            />
            <div>
              {isEditing ? (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={editableName}
                      onChange={(e) => {
                        setEditableName(e.target.value);
                        setError("");
                      }}
                      onBlur={handleBlur}
                      className={`border ${
                        error ? "border-red-500" : "border-gray-300"
                      } rounded-md px-2 py-1 text-3xl font-bold focus:outline-none focus:border-blue-500`}
                      autoFocus
                    />
                    <LoadingButton
                      variant="contained"
                      size="small"
                      onMouseDown={(e) => e.preventDefault()} // prevent blur before click
                      onClick={handleUpdateName}
                      disabled={!editableName.trim() || updateLoading}
                      sx={{
                        textTransform: "none",
                        bgcolor: editableName.trim() ? "#3b82f6" : "#93c5fd",
                        "&:hover": { bgcolor: "#2563eb" },
                      }}
                    >
                      {t("Save", "Enregistrer")}
                    </LoadingButton>
                  </div>
                  {error && (
                    <p className="text-red-500 text-sm mt-1 ml-1">{error}</p>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h1 className="text-4xl text-black font-bold font-Roboto">
                    {editableName}
                  </h1>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="text-gray-500 hover:text-blue-600"
                  >
                    <FaRegEdit className="text-lg" />
                  </button>
                </div>
              )}

              {/* WEBSITE URL */}
              <a
                href={projectInfos.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray hover:underline hover:text-black text-md"
              >
                {projectInfos.url}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* CMS CONNECTION */}
      <div>
        <div className="p-6 mb-5 mx-auto flex flex-col bg-white shadow-default rounded-2xl">
          <h2 className="text-xl font-semibold flex flex-row items-center gap-2">
            <CiPlug1 />
            {t("CMS Connection", "Connexion CMS")}
          </h2>
          <p className="text-sm mb-3 text-gray">
            {t(
              "Connect your CMS to automatically publish your content.",
              "Connectez votre CMS pour publier automatiquement votre contenu."
            )}
          </p>

          {/* ✅ Fixed condition for all platforms */}
          {isCMSConnected && (
            <div className="w-full border-b border-stone-200 my-2 pb-3">
              <p className="text-sm flex flex-row items-center gap-2 text-green-600">
                <FaRegCircleCheck />
                {t("CMS connection successful", "Connexion CMS réussie")}
              </p>
            </div>
          )}

          <div className="flex flex-col md:flex-row md:justify-between gap-4 w-full">
            <div className="w-full mt-2">
              <h2 className="text-md font-semibold ">
                {t("Default Export Settings", "Paramètres d’exportation par défaut")}
              </h2>
              <p className="text-sm mb-3 text-gray">
                {t(
                  "Choose the settings you want to apply by default when exporting content to your CMS.",
                  "Choisissez les paramètres à appliquer par défaut lors de l’exportation de contenu vers votre CMS."
                )}
              </p>

              {isCMSConnected ? (
                <ProjectSettingsForm
                  platform_name={projectInfos.platform}
                  exportData={export_data}
                  project={project}
                  setProject={setProject}
                />
              ) : (
                <div className="flex justify-start mt-4">
                  <LoadingButton
                    variant="contained"
                    onClick={handleOpenCMS}
                    sx={{
                      textTransform: "none",
                      bgcolor: "#8b5cf6",
                      "&:hover": { bgcolor: "#7c3aed" },
                    }}
                  >
                    {t("Connect a CMS", "Connecter un CMS")}
                  </LoadingButton>
                </div>
              )}
            </div>

            {/* MODIFY INTEGRATION SECTION */}
            {projectInfos?.platform !== "website" && (
              <div className="w-full">
                <div className="mt-2 mx-auto flex flex-col">
                  <h2 className="text-md font-semibold">
                    {t("Modify Integration", "Modifier l'intégration")}
                  </h2>
                  <ProjectIntegrationForm
                    platform_name={projectInfos?.platform}
                    integrationData={integration_data}
                    project={project}
                    setProject={setProject}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CMS SELECTION DIALOG */}
      <SelectPlatform
        open={openPlatformDialog}
        onClose={handleCloseCMS}
        integrationData={integration_data}
        project={project}
        setProject={setProject}
      />

      {/* CONFIRM NAME CHANGE DIALOG */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        className="relative z-[999]"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />

        <div className="fixed inset-0 z-[999] w-screen overflow-y-auto bg-black/50">
          <div className="flex min-h-full justify-center p-4 text-center items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-6 text-left shadow-xl transition-all sm:w-full sm:max-w-md sm:p-6"
            >
              {/* Close Button */}
              <button
                className="absolute top-3 right-4 text-gray-400 hover:text-gray-600 text-2xl"
                onClick={() => setOpenConfirmDialog(false)}
              >
                &#10005;
              </button>

              <DialogTitle
                as="h3"
                className="text-xl font-semibold text-gray-900 mb-4"
              >
                {t("Confirm Name Change", "Confirmer le changement de nom")}
              </DialogTitle>

              <p className="text-gray-700 mb-6 text-sm">
                {t(
                  "Do you want to change the website name or keep the previous one?",
                  "Voulez-vous changer le nom du site Web ou conserver l’ancien ?"
                )}
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={handleKeepPrevious}
                  className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300"
                >
                  {t("Keep Previous", "Garder l’ancien")}
                </button>
                <button
                  onClick={handleConfirmChange}
                  className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                >
                  {t("Change", "Changer")}
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Project;
